import React from 'react';
import { X, Image, FileText, Video, File } from 'lucide-react';
import { UploadedFile } from '../FileUpload';

interface FileItemProps {
    file: UploadedFile;
    disabled: boolean;
    onRemove: (id: string) => void;
    onDescriptionChange: (id: string, description: string) => void;
}

const getFileIcon = (type: UploadedFile['type']) => {
    switch (type) {
        case 'photo': return <Image className="w-5 h-5" />;
        case 'video': return <Video className="w-5 h-5" />;
        case 'document': return <FileText className="w-5 h-5" />;
        default: return <File className="w-5 h-5" />;
    }
};

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const FileItem: React.FC<FileItemProps> = ({
    file,
    disabled,
    onRemove,
    onDescriptionChange
}) => {
    return (
        <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex-shrink-0">
                {file.type === 'photo' && file.preview ? (
                    <img
                        src={file.preview}
                        alt={file.name}
                        className="w-16 h-16 object-cover rounded-lg"
                    />
                ) : (
                    <div className="w-16 h-16 flex items-center justify-center bg-gray-200 rounded-lg text-gray-500">
                        {getFileIcon(file.type)}
                    </div>
                )}
            </div>

            <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{file.name}</p>
                <p className="text-sm text-gray-500">
                    {formatFileSize(file.size)} • {file.type}
                </p>
                <input
                    type="text"
                    placeholder="Add description (optional)"
                    value={file.description || ''}
                    onChange={(e) => onDescriptionChange(file.id, e.target.value)}
                    className="mt-2 w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                    disabled={disabled}
                />
            </div>

            {!disabled && (
                <button
                    onClick={() => onRemove(file.id)}
                    className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove file"
                >
                    <X className="w-5 h-5" />
                </button>
            )}
        </div>
    );
};
